import React, { useMemo } from 'react'

import { ChainId, getChainNameFromChainId } from '@src/configs/chainInfo'
import CompactTokensList from '@src/pages/Exchange/components/TokenSelectorModal/components/CompactTokensList'
import { sortTokensByPriority } from '@src/pages/Exchange/components/TokenSelectorModal/utils'
import { IToken } from '@src/types/token'
import { isFeatureEnabled } from '@src/utils/environment'

interface TokenResultsDisplayProps {
    activeChainSelection?: number
    tokenQuery: string
    availableChains: Record<string, any>
    displayedTokens: IToken[]
    getTokenBalance?: (token: IToken) => { tokenBalance: string; usdBalance: string } | undefined
    selectedToken?: IToken
    onTokenClicked: (token: IToken) => void
    searchResult: IToken[]
    currentlySelectedToken?: IToken
}

const TokenResultsDisplay: React.FC<TokenResultsDisplayProps> = ({
    activeChainSelection,
    tokenQuery,
    availableChains,
    displayedTokens,
    getTokenBalance,
    selectedToken,
    onTokenClicked,
    searchResult,
    currentlySelectedToken,
}) => {
    // Create a unique token identifier
    const getTokenId = (token: IToken) => `${token.chainId}-${token.address.toLowerCase()}`

    // For chain-specific searches without an address query, use the regular display tokens
    const baseTokensToDisplay = activeChainSelection ? displayedTokens : searchResult

    // Get the trimmed, lowercase query for consistent comparison
    const normalizedQuery = tokenQuery.toLowerCase().trim()

    // Check if the query looks like an address (simple heuristic)
    const isAddressQuery = normalizedQuery.startsWith('0x') && normalizedQuery.length >= 40

    // Special handling for address queries to ensure the token is always displayed
    // when its address is pasted, regardless of what tokensToDisplay contains
    const addressMatchingToken = isAddressQuery
        ? // Check all available tokens in searchResult which might contain tokens from all chains
          searchResult.find(token => token.address.toLowerCase() === normalizedQuery)
        : undefined

    // Get the current chain name for display
    const getCurrentChainName = (chainId: number) => {
        const chainName = getChainNameFromChainId(chainId as ChainId)
        // Only capitalize the first letter of the chain name
        const formattedChainName = chainName.charAt(0).toUpperCase() + chainName.slice(1)
        if (formattedChainName === 'Bsc') return 'BSC'
        else if (formattedChainName === 'Bitcoin') return 'Bitcoin'
        return formattedChainName
    }

    // Prepare tokens to display with unique enforcement and special handling
    const allTokens = useMemo(() => {
        // Add address-matching token and current token if needed
        let combinedTokens = [...baseTokensToDisplay]

        if (addressMatchingToken) {
            combinedTokens.push(addressMatchingToken)
        }

        if (
            currentlySelectedToken &&
            !combinedTokens.some(token => getTokenId(token) === getTokenId(currentlySelectedToken))
        ) {
            // Only add if it matches the search query
            if (
                !tokenQuery ||
                currentlySelectedToken.name.toLowerCase().includes(normalizedQuery) ||
                currentlySelectedToken.symbol.toLowerCase().includes(normalizedQuery) ||
                currentlySelectedToken.address.toLowerCase() === normalizedQuery
            ) {
                combinedTokens.push(currentlySelectedToken)
            }
        }

        // In testnet mode, ensure uniqueness to prevent duplicates
        if (isFeatureEnabled('ENABLE_TESTNETS') || isFeatureEnabled('USE_TESTNETS_ONLY')) {
            const uniqueMap = new Map<string, IToken>()

            combinedTokens.forEach(token => {
                uniqueMap.set(getTokenId(token), token)
            })

            combinedTokens = Array.from(uniqueMap.values())
        }

        return combinedTokens
    }, [baseTokensToDisplay, addressMatchingToken, currentlySelectedToken, tokenQuery, normalizedQuery])

    // Empty state is now handled by the parent component
    if (allTokens.length === 0) {
        return null
    }

    // If no active chain selection, show all tokens in one list
    if (!activeChainSelection) {
        return (
            <div className='animate-fadeIn space-y-4'>
                {/* All tokens */}
                <div className='space-y-1'>
                    <div className='px-2'>
                        <h3 className='text-sm font-medium text-token-modal-label-text'>
                            {`Tokens (${allTokens.length})`}
                        </h3>
                    </div>
                    <CompactTokensList
                        tokens={sortTokensByPriority(allTokens, tokenQuery)}
                        selectedToken={selectedToken}
                        onTokenClicked={onTokenClicked}
                        getTokenBalance={getTokenBalance}
                        currentlySelectedToken={currentlySelectedToken}
                    />
                </div>
            </div>
        )
    }

    // Separate tokens by chain when a chain is selected
    const isTestnetMode = isFeatureEnabled('ENABLE_TESTNETS') || isFeatureEnabled('USE_TESTNETS_ONLY')

    const selectedChainTokens = allTokens.filter(token => Number(token.chainId) === activeChainSelection)

    // In testnet mode, don't show "other chain" tokens to avoid confusion
    const otherChainTokens = isTestnetMode
        ? []
        : allTokens.filter(token => Number(token.chainId) !== activeChainSelection)

    return (
        <div className='animate-fadeIn space-y-4'>
            {/* Selected chain tokens */}
            {selectedChainTokens.length > 0 && (
                <div className='space-y-1'>
                    <div className='px-2'>
                        <h3 className='text-sm font-medium text-token-modal-label-text'>
                            {`${getCurrentChainName(activeChainSelection)} tokens (${selectedChainTokens.length})`}
                        </h3>
                    </div>
                    <CompactTokensList
                        tokens={sortTokensByPriority(selectedChainTokens, tokenQuery)}
                        selectedToken={selectedToken}
                        onTokenClicked={onTokenClicked}
                        getTokenBalance={getTokenBalance}
                        currentlySelectedToken={currentlySelectedToken}
                    />
                </div>
            )}

            {/* Other chain tokens */}
            {otherChainTokens.length > 0 && (
                <div className='space-y-1'>
                    <div className='px-2'>
                        <h3 className='text-sm font-medium text-token-modal-label-text'>
                            {`Other tokens (${otherChainTokens.length})`}
                        </h3>
                    </div>
                    <CompactTokensList
                        tokens={sortTokensByPriority(otherChainTokens, tokenQuery)}
                        selectedToken={selectedToken}
                        onTokenClicked={onTokenClicked}
                        getTokenBalance={getTokenBalance}
                        currentlySelectedToken={currentlySelectedToken}
                    />
                </div>
            )}
        </div>
    )
}

export default TokenResultsDisplay
